import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack, TextField, Button, Box, Typography, Chip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { Container, DataGrid } from '@/components/common';
import { useFaqPosts, useDeleteFaqPost } from '@/hooks/boards.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { DateCell } from '@/components/pages/components';
import { LIST_VIEW_SESSION_KEYS } from '@/util/listViewSessionKeys';
import { useListViewSessionPersistence } from '@/util/useListViewSessionPersistence';

const FAQ_DEFAULT_PAGINATION = { page: 0, pageSize: 10 };
const FAQ_DEFAULT_FILTERS = { searchKeyword: '' };

/**
 * FAQ 관리 페이지
 */
const FaqPage = () => {
  const navigate = useNavigate();

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetListSession,
  } = useListViewSessionPersistence(
    LIST_VIEW_SESSION_KEYS.faq,
    FAQ_DEFAULT_PAGINATION,
    FAQ_DEFAULT_FILTERS
  );

  const searchKeyword = filters.searchKeyword ?? '';
  const setSearchKeyword = useCallback((v) => {
    setFilters((f) => ({ ...f, searchKeyword: typeof v === 'function' ? v(f.searchKeyword) : v }));
  }, [setFilters]);

  const queryParams = useMemo(() => ({
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
    ...(searchKeyword && { keyword: searchKeyword }),
  }), [paginationModel, searchKeyword]);

  const { data, isLoading } = useFaqPosts(queryParams);
  const deleteFaqMutation = useDeleteFaqPost();

  const total = data?.total ?? data?.data?.total ?? 0;
  const rowData = data?.list ?? data?.data?.list ?? [];

  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    if (!isLoading) setRowCountState(total);
  }, [total, isLoading]);

  const handleSearch = useCallback(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleReset = useCallback(() => {
    resetListSession();
  }, [resetListSession]);

  const handleRowClick = useCallback((params) => {
    navigate(`/community/faq/detail/${params.row.id ?? params.row.faqId}`);
  }, [navigate]);

  const handleDelete = useCallback((params, e) => {
    e?.stopPropagation();
    const id = params.row.id ?? params.row.faqId;
    const title = params.row.title ?? params.row.question ?? '-';
    confirmToast(`"${title}" FAQ를 삭제하시겠습니까?`, async () => {
      try {
        await deleteFaqMutation.mutateAsync(id);
        successToast('FAQ가 삭제되었습니다.');
      } catch (err) {
        errorToast(`FAQ 삭제에 실패했습니다. - ${err.response?.data?.msg}`);
      }
    });
  }, [deleteFaqMutation]);

  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.3,
      minWidth: 80,
      sortable: false,
    },
    {
      field: 'title',
      headerName: '제목',
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'categoryName',
      headerName: '카테고리',
      flex: 0.3,
      minWidth: 100,
      sortable: false,
      valueGetter: (value, row) => {
        if (!row) return '-';
        const type = row.category?.categoryType ?? row.categoryType ?? '';
        const name = row.category?.categoryName ?? row.categoryName ?? '';
        return [type, name].filter(Boolean).join(' - ') || '-';
      },
    },
    {
      field: 'answered',
      headerName: '답변 여부',
      flex: 0.3,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const hasAnswer = params.row.hasReply;
        return (
          <Chip
            label={hasAnswer ? '답변 완료' : '미답변'}
            size="small"
            color={hasAnswer ? 'success' : 'error'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: '등록일',
      flex: 0.3,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <DateCell row={params.row} dateField="createdAt" />
      ),
    },
    {
      field: 'actions',
      headerName: '작업',
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={(e) => handleDelete(params, e)}
        >
          삭제
        </Button>
      ),
    },
  ], [handleDelete]);

  return (
    <Container title="FAQ 관리">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="제목 검색"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 200 }}
          />
          <Button variant="outlined" onClick={handleSearch} sx={{ height: 40 }}>
            검색
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ height: 40 }}>
            초기화
          </Button>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/community/faq/new')}
          sx={{ height: 40 }}
        >
          FAQ 등록
        </Button>
      </Stack>

      <DataGrid
        height={'auto'}
        rows={rowData}
        columns={columns}
        loading={isLoading}
        rowCount={rowCountState}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row, index) => row.id ?? row.faqId ?? `row-${index}`}
        onRowClick={handleRowClick}
      />
    </Container>
  );
};

export default FaqPage;
